import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Sparkles, ChevronDown, ChevronUp, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useAuth } from '@/hooks/useAuth';
import { ReplyThreadDialog, isReplyRead } from './ReplyThreadDialog';
import { fetchOwnerReplyFeed } from '@/lib/replyFeed';

const copy = {
  en: { title: 'New Replies', subtitle: 'Fresh messages waiting for you', empty: 'No new replies — your words are still doing their work 💚', new: 'New', replied: 'replied', showMore: 'Show More', showLess: 'Show Less', from: 'Re:' },
  es: { title: 'Nuevas Respuestas', subtitle: 'Mensajes nuevos esperándote', empty: 'Sin nuevas respuestas — tus palabras siguen haciendo su trabajo 💚', new: 'Nuevo', replied: 'respondió', showMore: 'Ver más', showLess: 'Ver menos', from: 'Re:' },
  fr: { title: 'Nouvelles Réponses', subtitle: 'De nouveaux messages vous attendent', empty: 'Pas de nouvelles réponses — vos mots font encore leur effet 💚', new: 'Nouveau', replied: 'a répondu', showMore: 'Voir plus', showLess: 'Voir moins', from: 'Re :' },
  pt: { title: 'Novas Respostas', subtitle: 'Mensagens novas esperando você', empty: 'Sem novas respostas — suas palavras continuam trabalhando 💚', new: 'Novo', replied: 'respondeu', showMore: 'Ver mais', showLess: 'Ver menos', from: 'Re:' },
  ar: { title: 'ردود جديدة', subtitle: 'رسائل جديدة بانتظارك', empty: 'لا ردود جديدة — كلماتك لا تزال تعمل 💚', new: 'جديد', replied: 'رد', showMore: 'عرض المزيد', showLess: 'عرض أقل', from: 'رد:' },
  ja: { title: '新しい返信', subtitle: '新しいメッセージが届いています', empty: '新しい返信はありません — あなたの言葉はまだ届いています 💚', new: '新着', replied: 'が返信しました', showMore: 'もっと見る', showLess: '閉じる', from: 'Re:' },
  ko: { title: '새 답장', subtitle: '새 메시지가 기다리고 있어요', empty: '새 답장이 없습니다 — 당신의 말이 아직 전해지고 있어요 💚', new: '새로운', replied: '님이 답장', showMore: '더 보기', showLess: '접기', from: 'Re:' },
  id: { title: 'Balasan Baru', subtitle: 'Pesan baru menunggumu', empty: 'Tidak ada balasan baru — kata-katamu masih bekerja 💚', new: 'Baru', replied: 'membalas', showMore: 'Lihat Lebih', showLess: 'Tutup', from: 'Re:' },
  hi: { title: 'नए जवाब', subtitle: 'नए संदेश आपका इंतज़ार कर रहे हैं', empty: 'कोई नया जवाब नहीं — आपके शब्द अभी भी काम कर रहे हैं 💚', new: 'नया', replied: 'ने जवाब दिया', showMore: 'और दिखाएँ', showLess: 'कम दिखाएँ', from: 'पुनः:' },
};

interface ReplyWithContext {
  id: string;
  reply_text: string;
  created_at: string;
  recipient_name: string;
  announcement_id: string;
  source: 'direct' | 'thread';
}

interface RecentRepliesCardProps {
  announcements?: { id: string; recipient_name: string; status: string }[];
}

export function RecentRepliesCard({ announcements = [] }: RecentRepliesCardProps) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const c = lc(copy, language);
  const [replies, setReplies] = useState<ReplyWithContext[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [openAnnouncementId, setOpenAnnouncementId] = useState<string | null>(null);
  const [openRecipientName, setOpenRecipientName] = useState<string>('');
  const [refreshTick, setRefreshTick] = useState(0);

  const approvedAnnouncements = announcements.filter(a => a.status === 'approved');

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const fetchAllReplies = async () => {
      try {
        const approvedIds = new Set(approvedAnnouncements.map((a) => a.id));
        const feed = await fetchOwnerReplyFeed(user.id);
        const allReplies: ReplyWithContext[] = feed
          .filter((item) => approvedIds.size === 0 || approvedIds.has(item.announcement_id))
          .map((item) => ({
            id: item.id,
            reply_text: item.text,
            created_at: item.created_at,
            announcement_id: item.announcement_id,
            recipient_name: item.recipient_name,
            source: item.source === 'thread_messages' ? 'thread' : 'direct',
          }));

        const seen = new Set<string>();
        const unique = allReplies.filter(r => {
          if (seen.has(r.id)) return false;
          seen.add(r.id);
          return true;
        });
        unique.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        setReplies(unique.slice(0, 100));
      } catch (err) {
        console.error('Failed to load recent replies:', err);
        setReplies([]);
      }
      setIsLoading(false);
    };

    fetchAllReplies();
  }, [user, approvedAnnouncements.length, refreshTick]);

  // SECTION B = "New Replies" → only show UNREAD replies. Once opened, they move to Active Conversations.
  const unreadReplies = replies.filter((r) => !isReplyRead(r.id));

  if (isLoading) return null;
  if (unreadReplies.length === 0) return null;

  const visibleReplies = expanded ? unreadReplies : unreadReplies.slice(0, 3);

  return (
    <Card className="relative border-0 rounded-2xl overflow-hidden shadow-soft bg-gradient-to-br from-amber-500/10 via-card to-primary/10 ring-1 ring-amber-400/30" data-tour="replies">
      <div className="h-1 bg-gradient-to-r from-amber-400 via-primary to-amber-400 animate-pulse" />
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <div className="relative p-1.5 rounded-lg bg-amber-400/20">
            <Bell className="h-4 w-4 text-amber-500" />
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-400 animate-pulse ring-2 ring-background" />
          </div>
          <span>{c.title}</span>
          <Badge className="ml-auto rounded-lg text-xs bg-amber-500/90 hover:bg-amber-500 text-white border-0">
            {unreadReplies.length} {c.new}
          </Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground pl-9 -mt-1">{c.subtitle}</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <AnimatePresence mode="popLayout">
            {visibleReplies.map((reply, i) => (
              <motion.button
                key={reply.id}
                type="button"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: 20, scale: 0.96 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => {
                  setOpenAnnouncementId(reply.announcement_id);
                  setOpenRecipientName(reply.recipient_name);
                }}
                className="w-full text-left flex gap-3 p-3 rounded-xl bg-background/80 hover:bg-background active:bg-muted/40 transition-all border border-amber-400/20 hover:border-amber-400/50 group"
              >
                <div className="p-1.5 rounded-full bg-amber-400/20 h-fit mt-0.5 shrink-0">
                  <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-foreground">
                      {reply.recipient_name}
                    </span>
                    <span className="text-xs text-muted-foreground">{c.replied}</span>
                    <Badge className="text-[10px] h-4 px-1.5 rounded-md bg-amber-500/90 hover:bg-amber-500 text-white border-0">
                      {c.new}
                    </Badge>
                  </div>
                  <p className="text-sm text-foreground/90 leading-relaxed line-clamp-2 whitespace-pre-wrap break-words">
                    "{reply.reply_text}"
                  </p>
                  <p className="text-[11px] text-muted-foreground/70">
                    {new Date(reply.created_at).toLocaleDateString(undefined, {
                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-amber-500/60 group-hover:text-amber-500 self-center shrink-0" />
              </motion.button>
            ))}
          </AnimatePresence>

          {unreadReplies.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full gap-1.5 text-xs text-muted-foreground hover:text-foreground rounded-xl"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" />
                  {c.showLess}
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" />
                  {c.showMore} ({unreadReplies.length - 3})
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>

      <ReplyThreadDialog
        open={!!openAnnouncementId}
        onOpenChange={(o) => {
          if (!o) {
            setOpenAnnouncementId(null);
            // Force re-evaluation of read state — opened replies disappear from this notification feed
            // and move to "Active Conversations".
            setRefreshTick((t) => t + 1);
          }
        }}
        announcementId={openAnnouncementId}
        recipientName={openRecipientName}
      />
    </Card>
  );
}
